// frontend/src/super-admin/site/CustomerReviewsPage.tsx
//
// List + create + edit customer reviews shown on the marketing site.
// Gated by `site.reviews.write` for mutations.

import * as React from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Star, Eye, EyeOff, MessageSquareQuote } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { PageHeader } from '../../design-system/components/PageHeader';
import { LoadingState } from '../../design-system/components/LoadingState';
import { ErrorState } from '../../design-system/components/ErrorState';
import { ImageUploadInput } from '../../design-system/components/ImageUploadInput';
import { VISUAL, statusPill } from '../../design-system/visual';
import { reviewsApi, type CustomerReview, type CustomerReviewInput } from '../../modules/site-settings/api';
import { useAdminPermission } from '../../core/auth/useAdminPermission';
import { useConfirm } from '../../components/ui/confirm-dialog';

const EMPTY: CustomerReviewInput = {
  name: '',
  role: '',
  company: '',
  sector: '',
  city: '',
  rating: 5,
  body: '',
  avatar_url: '',
  is_published: false,
  sort_order: 100,
};

export function CustomerReviewsPage() {
  const confirm = useConfirm();
  const canWrite = useAdminPermission('site.reviews.write');
  const [reviews, setReviews] = React.useState<CustomerReview[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<{ kind: 'new' } | { kind: 'edit'; id: string } | null>(null);
  const [draft, setDraft] = React.useState<CustomerReviewInput>(EMPTY);
  const [busy, setBusy] = React.useState(false);

  function load() {
    setError(null);
    setReviews(null);
    reviewsApi.list()
      .then(setReviews)
      .catch((e) => setError(String(e.message ?? e)));
  }

  React.useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function startNew() {
    setDraft({ ...EMPTY });
    setEditing({ kind: 'new' });
  }

  function startEdit(r: CustomerReview) {
    setDraft({
      name: r.name,
      role: r.role ?? '',
      company: r.company ?? '',
      sector: r.sector ?? '',
      city: r.city ?? '',
      rating: r.rating,
      body: r.body,
      avatar_url: r.avatar_url ?? '',
      is_published: r.is_published,
      sort_order: r.sort_order,
    });
    setEditing({ kind: 'edit', id: r.id });
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setBusy(true);
    try {
      const payload: CustomerReviewInput = {
        ...draft,
        name: (draft.name ?? '').trim(),
        body: (draft.body ?? '').trim(),
      };
      if (editing.kind === 'new') {
        const created = await reviewsApi.create(payload);
        setReviews((prev) => (prev ? [created, ...prev] : [created]));
        toast.success('Review created');
      } else {
        const updated = await reviewsApi.update(editing.id, payload);
        setReviews((prev) => (prev ? prev.map((r) => (r.id === updated.id ? updated : r)) : prev));
        toast.success('Review updated');
      }
      setEditing(null);
    } catch (err: any) {
      toast.error('Save failed', { description: err.message ?? String(err) });
    } finally { setBusy(false); }
  }

  async function togglePublish(r: CustomerReview) {
    try {
      const updated = await reviewsApi.update(r.id, { is_published: !r.is_published });
      setReviews((prev) => (prev ? prev.map((x) => (x.id === updated.id ? updated : x)) : prev));
      toast.success(updated.is_published ? 'Published' : 'Unpublished');
    } catch (err: any) {
      toast.error('Update failed', { description: err.message ?? String(err) });
    }
  }

  async function remove(r: CustomerReview) {
    if (!(await confirm({ title: `Delete "${r.name}"'s review?`, description: 'This cannot be undone.', destructive: true, confirmLabel: 'Delete' }))) return;
    try {
      await reviewsApi.remove(r.id);
      setReviews((prev) => (prev ? prev.filter((x) => x.id !== r.id) : prev));
      toast.success('Review deleted');
    } catch (err: any) {
      toast.error('Delete failed', { description: err.message ?? String(err) });
    }
  }

  return (
    <div>
      <PageHeader
        eyebrow="Site / Content"
        title="Customer reviews"
        description="Quotes shown on the marketing site. Only published reviews appear publicly."
        actions={canWrite ? (
          <Button onClick={startNew} className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            New review
          </Button>
        ) : undefined}
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {error && <ErrorState description={error} onRetry={load} />}
        {reviews === null && !error && <LoadingState variant="page" />}

        {reviews && reviews.length > 0 && (
          <ul className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {reviews.map((r) => (
              <li key={r.id} className={[VISUAL.bento, 'p-6'].join(' ')}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[14.5px] font-semibold tracking-[-0.005em] text-foreground truncate">
                      {r.name}
                    </p>
                    <p className="text-[12px] text-muted-foreground truncate">
                      {[r.role, r.company].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </div>
                  <span className={statusPill(r.is_published ? 'success' : 'draft')}>
                    {r.is_published ? 'Published' : 'Draft'}
                  </span>
                </div>

                <blockquote className="mt-4 text-[13.5px] text-foreground/80 leading-relaxed">
                  "{r.body}"
                </blockquote>

                <div className="mt-4 flex flex-wrap items-center gap-2 text-[11.5px] text-muted-foreground">
                  {r.rating != null && (
                    <span className="inline-flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={'h-3 w-3 ' + (i < (r.rating ?? 0) ? 'fill-coral text-coral' : 'text-muted-foreground/40')}
                          strokeWidth={1.5}
                        />
                      ))}
                    </span>
                  )}
                  {r.sector && <span>{r.sector}</span>}
                  {r.city && <span>· {r.city}</span>}
                  <span className="ml-auto tabular-nums">sort {r.sort_order}</span>
                </div>

                {canWrite && (
                  <div className="mt-5 flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => togglePublish(r)}
                      className="h-9 px-3 text-[12.5px] font-semibold rounded-full gap-1.5"
                    >
                      {r.is_published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      {r.is_published ? 'Unpublish' : 'Publish'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => startEdit(r)}
                      className="h-9 px-3 text-[12.5px] font-semibold rounded-full"
                    >
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => remove(r)}
                      className="h-9 px-3 text-[12.5px] font-semibold rounded-full text-rose-700 hover:text-rose-800 gap-1.5"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {reviews && reviews.length === 0 && (
          <div className={[VISUAL.bento, 'py-14 px-8 text-center max-w-md mx-auto'].join(' ')}>
            <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-coral/15 ring-1 ring-coral/25 flex items-center justify-center">
              <MessageSquareQuote className="h-6 w-6 text-coral" strokeWidth={1.6} />
            </div>
            <p className="text-[18px] font-semibold tracking-[-0.01em] text-foreground">No reviews yet</p>
            <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">
              Add real customer quotes here. They appear on the marketing site once published.
            </p>
            {canWrite && (
              <Button onClick={startNew} className="mt-6 rounded-full h-10 px-5 font-semibold gap-1.5">
                <Plus className="h-3.5 w-3.5" /> Add first review
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex">
          <button
            type="button"
            aria-label="Close editor"
            onClick={() => setEditing(null)}
            className="absolute inset-0 bg-zinc-950/40 backdrop-blur-sm"
          />
          <form
            onSubmit={save}
            className="ml-auto relative w-full max-w-xl h-full bg-card overflow-y-auto shadow-2xl"
          >
            <div className="sticky top-0 z-10 bg-card/95 backdrop-blur-md border-b border-border/40 px-6 py-4 flex items-center justify-between">
              <div>
                <p className={VISUAL.eyebrow}>
                  {editing.kind === 'new' ? 'New review' : 'Edit review'}
                </p>
                <p className="mt-0.5 text-[16px] font-semibold tracking-[-0.005em] text-foreground">
                  {(draft.name?.trim() || 'Untitled')}
                </p>
              </div>
              <Button type="button" variant="ghost" onClick={() => setEditing(null)} className="h-9 px-3 text-[13px] rounded-full">
                Cancel
              </Button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid gap-1.5">
                <Label>Customer name *</Label>
                <Input value={draft.name ?? ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label>Role</Label>
                  <Input value={draft.role ?? ''} onChange={(e) => setDraft({ ...draft, role: e.target.value })} placeholder="Founder" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Company</Label>
                  <Input value={draft.company ?? ''} onChange={(e) => setDraft({ ...draft, company: e.target.value })} placeholder="Pinegate Studio" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label>Sector</Label>
                  <Input value={draft.sector ?? ''} onChange={(e) => setDraft({ ...draft, sector: e.target.value })} placeholder="Design studio" />
                </div>
                <div className="grid gap-1.5">
                  <Label>City</Label>
                  <Input value={draft.city ?? ''} onChange={(e) => setDraft({ ...draft, city: e.target.value })} placeholder="Portland, OR" />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label>Quote *</Label>
                <Textarea
                  value={draft.body ?? ''}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  rows={5}
                  placeholder="Worqly cut our Friday reconciliation from half a day to ten minutes."
                  required
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="grid gap-1.5">
                  <Label>Rating (1–5)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={5}
                    value={draft.rating ?? ''}
                    onChange={(e) => setDraft({ ...draft, rating: e.target.value === '' ? null : Number(e.target.value) })}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label>Sort order</Label>
                  <Input
                    type="number"
                    value={draft.sort_order ?? 100}
                    onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) })}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label>Status</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setDraft({ ...draft, is_published: !draft.is_published })}
                    className={
                      'h-10 rounded-2xl text-[13px] font-semibold gap-1.5 ' +
                      (draft.is_published
                        ? 'bg-lime-neon text-ink ring-1 ring-ink/10 hover:bg-lime-neon'
                        : 'bg-muted/60 text-muted-foreground ring-1 ring-border/40')
                    }
                  >
                    {draft.is_published ? 'Published' : 'Draft'}
                  </Button>
                </div>
              </div>
              <ImageUploadInput
                value={draft.avatar_url ?? ''}
                onChange={(url) => setDraft((d) => ({ ...d, avatar_url: url }))}
                endpoint="super-admin"
                area="reviews"
                label="Avatar"
              />
            </div>

            <div className="sticky bottom-0 bg-card/95 backdrop-blur-md border-t border-border/40 px-6 py-4 flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setEditing(null)} className="h-10 px-4 rounded-full text-[13px]">
                Cancel
              </Button>
              <Button type="submit" disabled={busy} className="h-10 px-5 rounded-full text-[13px] font-semibold">
                {busy ? 'Saving…' : editing.kind === 'new' ? 'Create review' : 'Save changes'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
